// The keyboard of a combobox whose text input keeps focus while a popup
// list shows options (WAI-ARIA combobox with aria-activedescendant): the
// want page's Add a card row and the card options' set search. Up/Down move
// the active option (skipping greyed ones, stopping at the ends), or open a
// closed list that has options; Enter picks; Escape closes an open list.
// Pure: the caller applies the action, keeps every key handled here from the
// browser (preventDefault), and stops the event where `stop` says so, an
// Escape that closed the list, so the dialog around it stays open.

/**
 * @typedef {{ type: 'open' } | { type: 'move', active: number } | { type: 'pick' }
 *   | { type: 'close' }} ComboboxAction
 */

/**
 * @typedef {Object} ComboboxState
 * @property {boolean} open - the list is showing
 * @property {number} active - the active option's index (-1: none)
 * @property {number} count - how many options the list holds
 * @property {(i: number) => boolean} [disabled] - option `i` is greyed
 */

/**
 * @param {string} key - the KeyboardEvent's `key`
 * @param {ComboboxState} state
 * @returns {{ action: ComboboxAction, stop: boolean } | null} null: not a
 *   combobox key, left alone
 */
export function comboboxKey(key, { open, active, count, disabled = () => false }) {
  if (key === 'Enter') return { action: { type: 'pick' }, stop: false };
  if (key === 'Escape') return open ? { action: { type: 'close' }, stop: true } : null;
  if ((key !== 'ArrowDown' && key !== 'ArrowUp') || count === 0) return null;
  if (!open) return { action: { type: 'open' }, stop: false };
  const step = key === 'ArrowDown' ? 1 : -1;
  for (let i = active + step; i >= 0 && i < count; i += step) {
    if (!disabled(i)) return { action: { type: 'move', active: i }, stop: false };
  }
  return { action: { type: 'move', active }, stop: false };
}

/**
 * The input's `aria-activedescendant`: option `active` of the list whose
 * options are `${listId}-${index}`, while the list is open.
 * @param {string} listId
 * @param {boolean} open
 * @param {number} active
 * @returns {string | undefined}
 */
export function activeDescendant(listId, open, active) {
  return open && active >= 0 ? `${listId}-${active}` : undefined;
}
